import { orgRoutes, type OrgLinkTarget } from '@/lib/routes'

/**
 * Where a `packages/web` URL lands in this app.
 *
 * Every one of these URLs is in somebody's bookmarks, in an email we already
 * sent, or in a tab that has been open since Thursday. The old app addressed
 * organizations by **uuid** and this one addresses them by **slug**, so the
 * mapping cannot be a static rewrite — it needs a database lookup, which is why
 * this file is only the pure half and the pages do the resolving.
 *
 * Derived from the real router in
 * `packages/web/src/modules/app/components/app-router/routes/`, not from
 * memory: the group tree there is recursive (`groups/:a/groups/:b/…`) and the
 * preferences tree is four levels deep, and both of those are exactly the parts
 * a hand-written list gets wrong.
 */

/**
 * A resolved destination.
 *
 * `organizationId` is the uuid that has to become a slug before `build` can
 * run. `null` means the URL named no organization — the caller's own default
 * workspace is the right answer, which is what the old chooser screens did too.
 */
export type LegacyRedirect = {
  organizationId: string | null
  /* An `OrgLinkTarget`, not a `string`: the router checks what reaches
     `redirect()` against the real route tree, so letting this widen would move
     a broken destination from a build error to a 404 in front of a customer. */
  build: (slug: string) => OrgLinkTarget
}

/** The old URLs carry uuids; anything else is not an id and must not be treated as one. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const isId = (segment: string | undefined): segment is string =>
  typeof segment === 'string' && UUID.test(segment)

/**
 * Walk the part of an `/organizations/:id/…` URL that comes after the org.
 *
 * The old tree nests arbitrarily — `groups/:a/groups/:b/constellations/:c/analytics`
 * is a legal URL — so this reads the whole path and keeps the LAST group and the
 * LAST constellation it sees. That matches how the old app rendered: the deepest
 * thing in the URL is the thing you were looking at, and the group above it is
 * the trail you walked to get there.
 */
function walkOrganizationSubtree(segments: string[], slug: string): OrgLinkTarget {
  const routes = orgRoutes(slug)

  let groupId: string | null = null
  let constellationId: string | null = null
  let leaf: 'analytics' | 'schedules' | null = null
  let archived = false

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i]

    if (segment === 'archived') {
      archived = true
      continue
    }
    if (segment === 'groups' && isId(segments[i + 1])) {
      groupId = segments[i + 1]
      i++
      continue
    }
    if (segment === 'constellations' && isId(segments[i + 1])) {
      constellationId = segments[i + 1]
      i++
      continue
    }
    if (segment === 'analytics' || segment === 'schedules') {
      leaf = segment
      continue
    }
    /* `dashboard` is the old name for the code's own screen, which is now the
       code route itself, so it needs no leaf. Anything else — a schedule id, a
       member id — is a deeper selection this app makes with state rather than a
       URL, and dropping it lands the user on the right screen. */
  }

  if (constellationId) {
    if (leaf === 'analytics') return routes.qrCodeAnalytics(constellationId)
    if (leaf === 'schedules') return routes.qrCodeSchedules(constellationId)
    /* The group is carried through as `?from=`, which is what makes the
       breadcrumb show the branch the user actually walked rather than the first
       one that happens to contain the code. */
    return groupId
      ? routes.qrCodeFrom(constellationId, groupId)
      : routes.qrCode(constellationId)
  }

  if (groupId) return routes.group(groupId)
  if (archived) return routes.archive

  return routes.qrCodes
}

/**
 * `/organizations/…` and `/admin/organizations/…`.
 *
 * The admin tree mounted the SAME organization routes under a different prefix,
 * so both arrive here with the prefix already stripped.
 */
export function resolveLegacyOrganizationsPath(
  segments: string[],
): LegacyRedirect {
  const [first, ...rest] = segments

  if (!isId(first)) {
    /* `/organizations` with no id was the old root redirect. A first segment
       that is present but is not a uuid is a URL we do not recognise, and the
       caller's own workspace beats a 404 either way. */
    return { organizationId: null, build: (slug) => orgRoutes(slug).qrCodes }
  }

  return {
    organizationId: first,
    build: (slug) => walkOrganizationSubtree(rest, slug),
  }
}

/**
 * `/admin/…`.
 *
 * The old admin tree mounted the SAME organization routes under
 * `/admin/organizations/:id/…`, so a platform administrator's deep link into
 * someone else's workspace is just an organizations URL with a different
 * prefix. Everything else in that tree — the bare directory, and `tools`, which
 * has no equivalent yet — resolves to the directory screen.
 */
export function resolveLegacyAdminPath(segments: string[]): LegacyRedirect {
  const [section, ...rest] = segments

  if (section === 'organizations' && rest.length > 0) {
    return resolveLegacyOrganizationsPath(rest)
  }

  return { organizationId: null, build: (slug) => orgRoutes(slug).admin }
}

/**
 * `/preferences/…`.
 *
 * The personal screens (account, profile, notifications) are dialogs in this
 * app rather than routes, so they resolve to the workspace the dialog opens
 * over. Sending someone to their codes is a better outcome than a 404 on a URL
 * that used to work, and the dialog is one click away.
 */
export function resolveLegacyPreferencesPath(
  segments: string[],
): LegacyRedirect {
  const [first, ...rest] = segments

  if (first !== 'enterprise') {
    return { organizationId: null, build: (slug) => orgRoutes(slug).qrCodes }
  }

  const [organizationId, section] = rest

  if (!isId(organizationId)) {
    /* `enterprise/settings|users-and-teams|billing` with no id was the
       "choose an organization" screen. This app has a workspace switcher
       instead, so the caller's own workspace is the honest landing point. */
    return { organizationId: null, build: (slug) => orgRoutes(slug).qrCodes }
  }

  return {
    organizationId,
    build: (slug) => {
      const routes = orgRoutes(slug)
      if (section === 'users-and-teams') return routes.members
      if (section === 'billing') return routes.billing
      /* `settings` — an org-settings dialog here — and the bare index. */
      return routes.qrCodes
    },
  }
}
